import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useTranslation } from 'react-i18next';

export default function SafetyPage() {
  const { t } = useTranslation('home');

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <div className="container-main pt-24 pb-16">
        <div className="max-w-3xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6">
            <a href="/" className="hover:text-primary-600 transition-colors">Home</a>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-600">Safety Guide</span>
          </nav>

          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-6">
            AllChinaBuy Safety Guide
          </h1>

          <div className="rounded-lg overflow-hidden mb-8">
            <img
              src="https://readdy.ai/api/search-image?query=Security%20and%20protection%20concept%20with%20shield%20and%20padlock%20on%20clean%20minimal%20white%20background%2C%20warm%20beige%20and%20cream%20tones%2C%20digital%20safety%20theme%2C%20editorial%20photography%20style%2C%20soft%20natural%20lighting%2C%20professional%20aesthetic%2C%20subtle%20gold%20accents&width=1200&height=500&seq=safety-hero-001&orientation=landscape"
              alt="AllChinaBuy Safety Guide"
              className="w-full h-56 md:h-72 object-cover"
            />
          </div>

          <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed">
            <p className="text-base mb-6">
              Buying through shopping agent platforms like AllChinaBuy requires attention to safety and security. This independent guide covers the most common risks and how to protect yourself throughout the buying process.
            </p>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">Platform Safety</h2>
            <p className="text-base mb-4">
              AllChinaBuy provides a built-in payment system that acts as an escrow. Your payment is held by the platform until you approve the QC photos. This is a meaningful safety feature that protects you from outright scams. However, always verify you are on the official website before logging in.
            </p>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">Common Risks to Avoid</h2>

            <div className="bg-background-100 rounded-lg p-5 border border-background-200/50 mb-6">
              <h3 className="font-medium text-sm text-foreground-900 mb-3">Fake Websites</h3>
              <p className="text-sm text-foreground-600 mb-3">Scammers create lookalike websites that mimic the official AllChinaBuy domain. Always double-check the URL before entering login credentials or payment information.</p>
              <p className="text-sm text-foreground-600">Bookmark the official site and access it only through your bookmark. Never click links from unsolicited emails or messages claiming to be from AllChinaBuy.</p>
            </div>

            <div className="bg-background-100 rounded-lg p-5 border border-background-200/50 mb-6">
              <h3 className="font-medium text-sm text-foreground-900 mb-3">Fake Coupons & Promotions</h3>
              <p className="text-sm text-foreground-600 mb-3">Beware of websites or social media posts offering unrealistic coupon codes. These often lead to phishing sites or scams. Only use promotions from official AllChinaBuy channels.</p>
            </div>

            <div className="bg-background-100 rounded-lg p-5 border border-background-200/50 mb-6">
              <h3 className="font-medium text-sm text-foreground-900 mb-3">Seller Bait-and-Switch</h3>
              <p className="text-sm text-foreground-600">Some sellers send different products than what was shown in their listings. This is why QC photos are essential — they are your opportunity to catch discrepancies before international shipping.</p>
            </div>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">Safety Checklist</h2>
            <div className="bg-accent-50 rounded-lg p-5 border border-accent-200/50 mb-6">
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-accent-100 text-accent-600 text-xs font-semibold flex-shrink-0">1</span>
                  <span className="text-sm text-accent-800">Verify the official website URL before every login</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-accent-100 text-accent-600 text-xs font-semibold flex-shrink-0">2</span>
                  <span className="text-sm text-accent-800">Never share your account password with anyone, including sellers</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-accent-100 text-accent-600 text-xs font-semibold flex-shrink-0">3</span>
                  <span className="text-sm text-accent-800">Only pay through the platform's built-in payment system</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-accent-100 text-accent-600 text-xs font-semibold flex-shrink-0">4</span>
                  <span className="text-sm text-accent-800">Research seller reputation before ordering</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-accent-100 text-accent-600 text-xs font-semibold flex-shrink-0">5</span>
                  <span className="text-sm text-accent-800">Always review QC photos carefully before approving shipment</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-accent-100 text-accent-600 text-xs font-semibold flex-shrink-0">6</span>
                  <span className="text-sm text-accent-800">Keep screenshots of all orders, communications, and QC photos</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-accent-100 text-accent-600 text-xs font-semibold flex-shrink-0">7</span>
                  <span className="text-sm text-accent-800">Start with small test orders from new sellers</span>
                </li>
              </ul>
            </div>

            <p className="text-base mb-6">
              For more detailed buying guidance, read our <a href="/allchinabuy-spreadsheet-complete-guide" className="text-primary-600 hover:underline">Complete Guide</a> or <a href="/is-allchinabuy-legit" className="text-primary-600 hover:underline">Is AllChinaBuy Legit?</a> article.
            </p>

            <div className="mt-10 bg-primary-50 rounded-lg p-6 md:p-8 text-center border border-primary-200/50">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Shop Safely</h3>
              <p className="text-sm text-foreground-600 mb-4">Browse our curated resources and find verified sellers.</p>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="btn-primary inline-flex">
                {t('cta.visit_main')}
                <i className="ri-external-link-line ml-1" />
              </a>
            </div>

            <div className="mt-10 pt-6 border-t border-background-200/50">
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">Related Articles</p>
              <div className="flex flex-wrap gap-2">
                <a href="/allchinabuy-spreadsheet-complete-guide" className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors">Complete Guide</a>
                <a href="/is-allchinabuy-legit" className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors">Is AllChinaBuy Legit?</a>
                <a href="/allchinabuy-shipping-cost-guide" className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors">Shipping Guide</a>
                <a href="/allchinabuy-qc-guide" className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors">QC Guide</a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}